import { Component, OnInit, OnChanges, OnDestroy, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import * as moment from 'moment';
import SwalComponent from './../../utils/custom-swal.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { FormService } from './../../api/form.service';
import { TransactionService } from '../../api/transaction.service';
import uuid from '../../utils/uuid';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Countries } from '../../utils/countries-enum';

@UntilDestroy()
@Component({
  selector: 'iv-rent-review',
  templateUrl: './rent-review.component.html',
  styleUrls: ['./rent-review.component.scss']
})
export class RentReviewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() index = 0;
  @Input() rentReview;
  @Input() lease;
  @Input() disabled = false;
  @Input() getUserCountry;
  @Output() saveRentReviewEvent = new EventEmitter();
  @Output() deleteRentReviewEvent = new EventEmitter();

  @ViewChild('warnDateValidAlert', { static: true }) warnDateValidAlert: SwalComponent;
  @ViewChild('warnRentReviewDateValidAlert', { static: true }) warnRentReviewDateValidAlert: SwalComponent;
  @ViewChild('rentReviewDateAlert', { static: true }) rentReviewDateAlert: SwalComponent;

  uuid = uuid();
  rentReviewForm: UntypedFormGroup;
  $rentReviewFormChanges;

  spaceTypes = [];
  rentFrequency = [];
  rentType = [];
  swalOptions: any;

  constructor(
    private fb: UntypedFormBuilder,
    private formService: FormService,
    private transactionService: TransactionService) {
    this.swalOptions = this.getSwalOptions();
  }

  ngOnInit() {
    // Getting field values
    this.formService.getFieldOptions('rent_review', 'spacetypes')
      .pipe(untilDestroyed(this))
      .subscribe(spacetypes => {
        this.spaceTypes = spacetypes;
      });
    this.formService.getFieldOptions('rent_review', 'frequency')
      .pipe(untilDestroyed(this))
      .subscribe(frequency => {
        this.rentFrequency = frequency;
      });
    this.formService.getFieldOptions('rent_review', 'type')
      .pipe(untilDestroyed(this))
      .subscribe(type => {
        this.rentType = type;
      });
    this.setupForm();
  }

  ngOnChanges() {
  }

  ngOnDestroy() {
    if (this.$rentReviewFormChanges) {
      this.$rentReviewFormChanges.unsubscribe();
    }
  }

  setupForm() {
    this.rentReviewForm = this.fb.group({
      space_type: [{ value: this.rentReview.space_type || '', disabled: this.disabled }, Validators.required],
      // review_date_check: [{ value: null, disabled: this.disabled }],
      // review_date: [{value: this.rentReview.review_date ? moment(this.rentReview.review_date) : null, disabled: this.disabled}],
      review_date: [{ value: this.rentReview.review_date ? moment(this.rentReview.review_date) : null, disabled: this.disabled }],
      frequency: [{ value: this.rentReview.frequency || '', disabled: this.disabled }, Validators.required],
      type: [{ value: this.rentReview.type || '', disabled: this.disabled }, Validators.required],
      review_amount: [{ value: this.rentReview.review_amount || '0.00', disabled: this.disabled }],
      pct: [{
        value: this.rentReview.pct || '0', disabled: this.disabled
      }, Validators.compose([Validators.min(0), Validators.max(100)])]
    });

    this.formService.addLoadedForm(this.rentReviewForm, this.uuid);
    this.setupRentReviewSubscription();
  }

  setupRentReviewSubscription() {
    if (this.$rentReviewFormChanges) {
      this.$rentReviewFormChanges.unsubscribe();
    }
    this.$rentReviewFormChanges = this.rentReviewForm.valueChanges
      .pipe(
        debounceTime(850),
        distinctUntilChanged()
      )
      .subscribe(changes => {
        let rentTypeChanges;
        if (changes && changes.type && changes.type !== this.rentReview.type) {
          rentTypeChanges = this.patchFields(changes.type, changes);
        }
        if (changes.review_date) {
          changes.review_date = moment(changes.review_date).format('YYYY-MM-DD');
          this.validateReviewDateForleaseStartEndDates(changes.review_date, this.rentReview);
        }
        if (changes.review_date !== 'Invalid date') {
          this.rentReview = { ...changes, ...rentTypeChanges, 'id': this.rentReview.id };
          this.saveRentReviewEvent.emit(this.rentReview);
        }
      });
  }

  deleteRentReview(rentReview) {
    this.deleteRentReviewEvent.emit(rentReview);
  }

  isValidDate(event) {
    if (event && event.targetElement && event.targetElement.value) {
      let isValid;
      let regexp;
      regexp = this.transactionService.dateValidator(this.getUserCountry);
      isValid = regexp.test(event.targetElement.value);
      if (isValid) {
        this.rentReview.review_date = this.transactionService.dateConvertor(this.getUserCountry, event.targetElement.value, 'rent-review');
        this.rentReviewForm.patchValue({
          'review_date': this.rentReview.review_date
        }, { emitEvent: false });
        this.rentReview.review_date = moment(event.targetElement.value).format('YYYY-MM-DD');
        if (this.rentReview.review_date !== 'Invalid date') {
          this.saveRentReviewEvent.emit(this.rentReview);
        }
      }
      if (!isValid) {
        this.warnDateValidAlert.fire().then(response => {
          if (response.value || response.dismiss) {
            this.rentReviewForm.patchValue({
              review_date: null
            });
          }
        });
      }
    }
  }
  patchFields(rentType, changes) {
    switch (rentType) {
      case 'CPI':
      case 'Market':
      case 'CPI+':
      case 'Fixed':
        this.rentReviewForm.patchValue({
          'review_amount': '0.00',
          'pct': '0'
        }, { emitEvent: false });
        changes.pct = '0';
        changes.review_amount = '0.00';
        break;
      default:
        break;
    }
    return changes;
  }
  validateReviewDateForleaseStartEndDates(rentReviewDate, rentReview) {
    switch (window.localStorage.getItem('country')) {
      case Countries.Japan:
      case Countries.Singapore:
      case Countries.Indonesia:
      case Countries.Hongkong:
      case Countries.China:
      case Countries.Thailand:
      case Countries.Philippines:
      case Countries.Vietnam:
      case Countries.Korea:
      case Countries.Taiwan:
        if (this.lease.start_date === null || this.lease.end_date === null) {
          this.rentReviewDateAlert.fire().then(response => {
            if (response.value || response.dismiss) {
              this.rentReviewForm.patchValue({
                review_date: null
              });
            }
          });
        }
        if (this.lease.start_date && this.lease.end_date) {
          const isBetween = moment(rentReviewDate)
            .isBetween(this.lease.start_date,
              this.lease.end_date, null, '[]');
          if (!isBetween) {
            this.warnRentReviewDateValidAlert.fire().then(response => {
              if (response.value || response.dismiss) {
                this.rentReviewForm.patchValue({
                  review_date: null
                });
              }
            });
          }
        }
        break;
      default:
        this.saveRentReviewEvent.emit(rentReview);
        break;
    }
  }
  getSwalOptions() {
    // {showConfirmButton: true, confirmButtonText: 'Continue', showCancelButton: true, showCloseButton: true}
    const options = {
      showConfirmButton: true,
      showCloseButton: true,
      confirmButtonText: 'Continue',
      cancelButtonText: 'Cancel',
      showCancelButton: true
    };
    return this.formService.getSwalOptions(options);
  }
}
